import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { DirProposalType } from '@econtractor/types';
import * as apiProposal from 'api/apiProposal';
import { useToast } from 'context/ToastContext';
import { useAuth } from 'context/AuthContext';
import { useModal } from 'context/ModalContext';
import { getButtonsNoYesDanger } from 'context/helpers/helperModalContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import ESpinner from 'components/others/Spinner';
import SvgTrash from 'components/svg/SvgTrash';
import ProposalEditorOffcanvas from './ProposalEditorOffcanvas';
import ProposalCard from './ProposalCard';

const Proposal = () => {
    const { t } = useTranslation(['Proposal', 'common']);
    const { showToast } = useToast();
    const { showDlg } = useModal();
    const { auth } = useAuth();
    const [loading, setLoading] = useState(false);
    const [proposals, setProposals] = useState<DirProposalType[] | null>(null);
    const [editProposalId, setEditProposalId] = useState<number | null>(null);
    const canEdit = hasUserAccess(auth?.user, 'prodEdit');

    const fetchProposals = async () => {
        setLoading(true);
        const result = await apiProposal.getDirList({
            order: 'ORDER BY proposal_updated DESC',
            onPage: 999999,
            page: 1,
            proposalType: 'proposal',
        });
        setLoading(false);
        if (!result) {
            showToast(t('common:messError'), 'danger');
            return;
        }
        setProposals(result.proposals || []);
    };

    const deleteProposal = async (proposalId: number) => {
        const stat = await apiProposal.deleteDirProposal(proposalId);
        if (!stat) {
            showToast(t('common:messError'), 'danger');
            return;
        }
        showToast(t('messDeleted'), 'success');
        await fetchProposals();
    };

    const handleDelete = (proposal: DirProposalType) => {
        const name = proposal.proposalName || t('card.noName');
        const params = {
            buttons: [...getButtonsNoYesDanger(t)],
            type: 'confirm' as const,
            text: (
                <>
                    <h4 className="d-flex pb-2 mb-2 border-bottom text-danger">
                        <SvgTrash className="text-danger" />
                        <span className="ps-2">{t('confirmDelete')}</span>
                    </h4>
                    <h5 className="mb-0">{name}</h5>
                    {proposal.proposalComment ? (
                        <small className="form-text text-muted">{proposal.proposalComment}</small>
                    ) : null}
                </>
            ),
        };
        params.buttons[1].handleClick = () => {
            showDlg(false);
            void deleteProposal(proposal.ID);
        };
        showDlg(params);
    };

    const handleDuplicate = (proposalId: number) => {
        setEditProposalId(-proposalId);
    };

    useEffect(() => {
        if (!auth?.user?.admin) return;
        void fetchProposals();
    }, [auth?.user?.admin]);

    const empty = useMemo(() => !loading && proposals && proposals.length === 0, [loading, proposals]);

    if (!auth?.user?.admin) {
        return (
            <Row>
                <Col sm={12}>
                    <Card>
                        <Card.Body>
                            <div className="alert alert-warning mb-0">{t('common:messReadOnly')}</div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        );
    }

    return (
        <>
            <ProposalEditorOffcanvas
                mode="dir"
                proposalId={editProposalId}
                onSaved={fetchProposals}
                onClose={() => setEditProposalId(null)}
            />

            <Row>
                <Col className="col-sticky" sm={12}>
                    <div className="w-100 d-sm-flex pt-1 justify-content-between align-items-start">
                        <div>
                            <h4 className="p-0 m-0 mb-0 me-2 text-break">{t('title')}</h4>
                            <small className="text-muted">{t('subtitle')}</small>
                        </div>
                        <div className="mt-2 mt-sm-0">
                            <div className="page-toolbar-actions gap-1">
                                {canEdit ? (
                                    <Button className="fs-14 ms-1" size="sm" variant="success" onClick={() => setEditProposalId(0)}>
                                        <i className="bi bi-plus fs-14 p-0"></i>
                                        <span className="ms-1">{t('toolCreate')}</span>
                                    </Button>
                                ) : null}
                                <Button size="sm" onClick={() => void fetchProposals()}>
                                    <i className="bi bi-arrow-clockwise fs-14 p-0"></i>
                                </Button>
                            </div>
                        </div>
                    </div>
                </Col>

                <Col sm={12}>
                    {loading || proposals === null ? (
                        <ESpinner />
                    ) : null}

                    {empty ? <div className="text-muted mt-2">{t('empty')}</div> : null}

                    {proposals?.length ? (
                        <Row className="g-3 mt-1">
                            {proposals.map((proposal) => (
                                <Col key={proposal.ID} xs={12} md={6} xl={4}>
                                    <ProposalCard
                                        proposal={proposal}
                                        canEdit={canEdit}
                                        onEdit={setEditProposalId}
                                        onDuplicate={handleDuplicate}
                                        onDelete={handleDelete}
                                    />
                                </Col>
                            ))}
                        </Row>
                    ) : null}
                </Col>
            </Row>
        </>
    );
};

export default Proposal;
